import { Box } from '@mui/material';
import { useTheme } from "@mui/material/styles";

import ColorUtil from '@plannel/pages/utils/color-util';
import CodeUtil from '@plannel/pages/utils/code-util';
import numberUtil from '@plannel/utils/number-util';
import { isEmpty, isEqual } from 'lodash';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
// import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";

/* IP Tab Name */
const IP_TARGET_STOCK = "IP_TARGET_STOCK";
const IP_STOCKOUT = "IP_STOCKOUT";
const IP_EXCESS = "IP_EXCESS";
const IP_SLOW_MOVING = "IP_SLOW_MOVING";

/* RP Tab Name */
const RP_ARRIVAL = "ARRIVAL";
const RP_PURCHASE = "PURCHASE";
const RP_SUPPLY = "SUPPLY";

/*DP Tab Name */
const DP_YOY = "YOY";
const DP_DP = "DP";
const DP_BUDGET = "BUDGET";

/* stack bar array measure 값  */
const MODULE_DATA_KEY_MAP = {
  /* IP Measure */
  [IP_TARGET_STOCK]: ["targetInventory", "actualInv", "percent"],
  [IP_STOCKOUT]: ["actualInv", "stockoutInventory", "percent"],
  [IP_EXCESS]: ["actualInv", "excessInventory", "percent"],
  [IP_SLOW_MOVING]: ["actualInv", "obsolescence", "percent"],
  /* RP Measure */
  [RP_ARRIVAL]: ["actArrival", "arrivalPlan", "percent"],
  [RP_PURCHASE]: ["purchasePlan", "purchaseBudget", "percent"],
  [RP_SUPPLY]: ["actShipment", "shipmentPlan", "percent"],
  /* DP Measure */
  [DP_YOY]: ["salesActual", "yoy", "percent"],
  [DP_DP]: ["salesActual", "demandPlan", "percent"],
  [DP_BUDGET]: ["salesActual", "budgetPlan", "percent"],

  /* Default */
  DEFAULT: ['plan', 'target', 'percent']
}

// 색 인덱스만 두고, 실제 색은 렌더 시점에 ColorUtil.colorSet 으로 계산한다.
//   (모듈 로드 시 상수로 굳히면 사용자 팔레트 변경/프리셋이 반영되지 않음)
const TAB_COLOR_INDEX = {
  [IP_STOCKOUT]: [0, 2],
  [IP_EXCESS]: [0, 2],
  [IP_SLOW_MOVING]: [0, 2],

  /* Default */
  DEFAULT: [1, 0],
}

/* Data를 보낼때 [tabName] props가 필요함 */
const StackBarChartCellRenderer = (props) => {
  const userData = JSON.parse(localStorage.getItem("user"));
  const currencySymbol = props?.currencyCd ? CodeUtil.getCurrencySymbol(props?.currencyCd) : userData?.currencySymbol ?? "$";
  const { toggleType: { AMT } } = CodeUtil;

  const { t } = useTranslation();
  const theme = useTheme();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const tooltipTimer = useRef(null);


  // const {DP, RP, IP} = CodeUtil.module
  // Assuming the value is an array of objects like [{name: 'A', value1: 30, value2: 70}]

  // const scmModule = props?.scmModule;
  const flipZIndex = props?.flipZIndex;
  const cellData = props?.data;
  const tabName = cellData?.tabName;
  const chartData = cellData?.stackBarChartData;
  const domainMaxValue = cellData?.domainMaxValue;

  const tabDataKey = MODULE_DATA_KEY_MAP?.[tabName] || MODULE_DATA_KEY_MAP.DEFAULT;
  const colorIndex = TAB_COLOR_INDEX?.[tabName] || TAB_COLOR_INDEX.DEFAULT;
  const colorKey = [ColorUtil.colorSet(colorIndex[0]), ColorUtil.colorSet(colorIndex[1])];

  const key1 = t(tabDataKey[0]);
  const key2 = t(tabDataKey[1]);
  const percent = t(tabDataKey[2]);
  const tabData = [{
    [key1]: chartData?.[0],
    [key2]: chartData?.[1],
    [percent]: chartData?.[2] ?? 0
  }]

  const baseBarKey = flipZIndex ? key1 : key2;
  const compareBarKey = flipZIndex ? key2 : key1;
  const maxDomain = !isEmpty(domainMaxValue) ? [0, tabData[0]] : [0, domainMaxValue];

  const tooltipHandler = () => {
    setTooltipOpen(!tooltipOpen);

    clearTimeout(tooltipTimer.current);
    if (!tooltipOpen) {
      /* Open되고 5초뒤 사라짐 */
      tooltipTimer.current = setTimeout(() => {
        setTooltipOpen(false);
      }, 5000);
    }
  }

  const chartFormatter = (value, _name, _props) => {
    if (isEqual(cellData.type, AMT)) {
      const formatValue = numberUtil.formatNumberData({ value }, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
      return value >= 0 ? currencySymbol + formatValue : `-${currencySymbol}${formatValue?.replace("-", "")}`;
    }
    return numberUtil.formatNumberData({ value }, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
  }

  return (
    <Box style={{ width: '100%', height: '100%' }}>
      <ResponsiveContainer width="100%" height="100%" paddingRight="20px">
        <BarChart data={tabData} layout="vertical" margin={{ top: 0, right: 60, left: 0, bottom: 0 }}
          onClick={tooltipHandler}
        >
          <XAxis type="number" domain={maxDomain} hide />
          <YAxis yAxisId={0} type="category" dataKey="name" hide />
          <YAxis yAxisId={1} type="category" dataKey="name" hide />
          {/*
            * 그리드 셀 안이라 좁다 — ag-charts 의 compact 변형을 쓰되 한 번 더 조인다.
            * 이 툴팁은 셀(rowHeight 35px, DashboardComponent) 안에 갇혀 잘리므로 2행이 그 안에 들어가야 한다.
            *   compact 기본: 테두리 2 + 패딩 6 + 간격 2 + 행 14.4×2 ≈ 39px  → 둘째 행이 잘린다
            *   아래 값   : 테두리 2 + 패딩 2 + 간격 2 + 행 12×2   = 30px  → 들어간다
            * lineHeight 1 이면 gridAutoRows 의 minmax(1em, auto) 가 1em(12px)으로 떨어진다.
            * ag 규격에서 벗어나는 건 셀 높이 때문이다 — 셀 밖으로 빼는 건 안 된다(위 주석의 allowEscapeViewBox 시도 흔적).
            */}
          <Tooltip
            isAnimationActive
            active={tooltipOpen}
            offset={0}
            content={<AgStyleTooltipContent compact />}
            contentStyle={{ padding: "1px 6px", lineHeight: 1 }}
            formatter={chartFormatter}
          // viewBox={{ x: 0, y: 50, width: 400, height: 50 }}
          // allowEscapeViewBox={{ x: true, y: true}}
          // coordinate={{ y: 100 }}
          />
          <Bar yAxisId={0} dataKey={baseBarKey} fill={colorKey[0]} />
          <Bar yAxisId={1} dataKey={compareBarKey} fill={colorKey[1]}>
            <LabelList dataKey={percent} position="right" fill={theme.palette.text.primary} formatter={val => `${val}%`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default StackBarChartCellRenderer;